/**
 * Which pane of a site the Deliver screen is showing.
 *
 * The site view was five stacked cards doing four unrelated jobs — what is
 * published, what is queued, how the site is built, how it is configured — with
 * the most useful of them (Structure) last, under a header card that expands to
 * a 165-line form. Reordering only moves the burial; these are different
 * questions and they get different panes.
 *
 * Pure and dependency-free so CRA's jest can test it: react-router-dom v7 does
 * not resolve under this jest config, so anything a routed component imports
 * has to live apart to be testable. Same reason `trendFreshness.ts` and
 * `navMemory.ts` exist.
 */

export type DeliverTab = 'structure' | 'publishing' | 'search' | 'target' | 'settings';

export const DELIVER_TABS: {
  key: DeliverTab;
  label: string;
  /** Only meaningful for a site we generate — a repo to commit into and a
   *  designer to shape. A WordPress theme is the customer's.
   *
   *  Nothing uses this any more and it is kept as the reason the mechanism
   *  exists: `target` WAS Hugo-only, until it took ownership of the Site Type
   *  selector. See `resolveDeliverTab`. */
  hugoOnly?: boolean;
}[] = [
  { key: 'structure', label: 'Structure' },
  { key: 'publishing', label: 'Publishing' },
  // WHAT SEARCH MADE OF IT: the Search Console connection, impressions, and the
  // 28/56-day queues — pages to retarget, pages to retire.
  { key: 'search', label: 'Search' },
  // HOW THIS SITE PUBLISHES: its type, the credentials or keys that type needs,
  // and — for a site we generate — the repo it commits into and the designer.
  //
  // NOT Hugo-only, though it holds the Hugo-only surfaces. It owns the Site Type
  // selector, and a tab that disappears for non-Hugo sites would take the only
  // way to make a site Hugo with it: switch a site to WordPress and you could
  // never switch it back.
  { key: 'target', label: 'Target' },
  { key: 'settings', label: 'Settings' },
];

export const DEFAULT_DELIVER_TAB: DeliverTab = 'structure';

/** Is this tab offered for a site of this type? */
export function tabAllowed(key: DeliverTab, siteType: string | null): boolean {
  const tab = DELIVER_TABS.find((t) => t.key === key);
  if (!tab) return false;
  return !tab.hugoOnly || siteType === 'HUGO';
}

/** The tabs to render for a site of this type, in order. */
export function tabsFor(siteType: string | null): typeof DELIVER_TABS {
  return DELIVER_TABS.filter((t) => tabAllowed(t.key, siteType));
}

/** Tab keys that have been renamed, so old links keep working. */
const LEGACY_TABS: Record<string, DeliverTab> = {
  // Was Hugo-only and called "Repository & design", before it took the Site
  // Type selector and had to start existing for every site.
  repo: 'target',
};

/**
 * Read the tab out of the URL, falling back rather than rendering nothing.
 *
 * Modelled on `resolveTrendsTab` — a cast would let `?tab=nonsense` through and
 * every pane would test false, leaving a header and blank space. The cases that
 * are not merely defensive:
 *
 * - **A tab that is not on offer for this site type.** No tab is gated today,
 *   but the mechanism stays: a bookmark saved before someone flipped the type,
 *   or a link from a colleague whose site is a different type, must not land on
 *   a pane with no tab to click out of.
 * - **Structure as the fallback, always.** It is the pane that answers "what is
 *   this site", it is the one that was buried, and a deterministic default beats
 *   a data-dependent one — landing somewhere you did not ask for is the more
 *   confusing failure. `navMemory` makes the same argument for its own default.
 *
 * The plan view is not a tab. `?plan=` addresses one deployment plan, which is a
 * whole screen rather than a pane of this one, and it predates the tabs — so it
 * wins outright and callers should check `hasPlan` before asking for a tab.
 */
export function resolveDeliverTab(
  params: { get(key: string): string | null },
  siteType: string | null,
): DeliverTab {
  const param = params.get('tab');
  const raw = (param && LEGACY_TABS[param]) || param;
  const known = DELIVER_TABS.find((t) => t.key === raw);
  if (!known) return DEFAULT_DELIVER_TAB;
  if (!tabAllowed(known.key, siteType)) return DEFAULT_DELIVER_TAB;
  return known.key;
}

/** Does this URL address a single deployment plan, rather than a site pane? */
export function hasPlan(params: { get(key: string): string | null }): boolean {
  const raw = params.get('plan');
  return !!raw && Number.isFinite(Number(raw)) && Number(raw) > 0;
}
